import { useCallback, useLayoutEffect, useState } from 'react'

interface UsePinAnchorsHeightResult {
  height: number | undefined
  recompute: () => void
}

export default function usePinAnchorsHeight(
  containerRef: React.RefObject<HTMLElement | null>,
  selector = '.pin-anchor',
): UsePinAnchorsHeightResult {
  const [height, setHeight] = useState<number>()

  const measure = useCallback(() => {
    const container = containerRef?.current
    if (!container) return

    const containerRect = container.getBoundingClientRect()
    const anchors = Array.from(container.querySelectorAll(selector))

    const maxY = anchors.reduce((max, element) => {
      const elementRect = element.getBoundingClientRect()
      const centerY = (elementRect.top - containerRect.top) + elementRect.height / 2
      return Math.max(max, centerY)
    }, 0)

    setHeight(maxY || undefined)
  }, [containerRef, selector])

  useLayoutEffect(() => {
    measure()

    const resizeObserver = new ResizeObserver(measure)
    const container = containerRef.current

    if (container) {
      resizeObserver.observe(container)
    }

    window.addEventListener('resize', measure)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure, containerRef])

  return { height, recompute: measure }
}
